import { useQuery } from '@tanstack/react-query'; // Note that we are NOT using our hand-rolled one because this doesn't return Response.data
import type { QueryKey } from '@tanstack/react-query';

import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { License, LicenseSummary } from '../../../types';

//  We can keep this data around for a bit longer since license data basically never changes.
const STALE_TIME = 1000 * 60 * 10; // 10 minutes

type LicenseResponse = License & {
  Summary?: LicenseSummary;
};

//  This is what is actually returned in the hook,
//  which is a slimmed down version of the full License type.
type LicenseData = {
  name: string;
  description?: string;
};

/**
 * Takes a license response and slims it down to the LicenseData type.
 * Will try to use the included `Summary` if available, otherwise falls back to `licenseComments`.
 */
function formatLicence({ name, Summary, licenseComments }: LicenseResponse): LicenseData {
  return {
    name,
    description: Summary?.summary || licenseComments,
  };
}

/**
 * Async function to fetch data about a license., format it, and return it.
 *
 * @param {string} licenseId The ID of the license to fetch.
 * @returns {Promise<LicenseData>} The formatted license data.
 */
async function getLicense(licenseId: string) {
  const { data } = await get<LicenseResponse>(`/api/licenses/${nuc(licenseId)}`);
  if (!data) {
    throw new Error(`License "${licenseId}" could not be fetched!`);
  }
  return formatLicence(data);
}

const getQueryKey = (licenseId: string): QueryKey => ['license', licenseId];

/**
 * Hook which will fetch data about a given license.
 * Caches the license data for 10 minutes, since it's unlikely to ever change.
 *
 * @param {string} licenseId The ID of the license to fetch.
 */
export default function useLicense(licenseId: string) {
  return useQuery<LicenseData>({
    queryKey: getQueryKey(licenseId),
    queryFn: () => getLicense(licenseId),
    staleTime: STALE_TIME,
  });
}
